/* Notification Center — host page (AI-only design module, demo data).
 *
 * Structured like Performance Analytics: the sub-modules live as nav items in
 * the app's OWN left sidebar (`grp-notifications` in layouts/navData.ts), and
 * this component renders just the ONE selected sub-page (`subId`).
 *
 * All toolbar buttons are wired (parity with the standalone HTML prototype):
 * New opens the wizard / entity form, Edit edits the selected row, Delete asks
 * for confirmation then removes, Refresh / Export / Import raise the shared
 * toast — via useNotify / useConfirm.  Mutations go through the Zustand store
 * (features/notifications/store.ts) so they persist across sub-page nav. */
import { useState, type ReactNode } from 'react';
import './notifications.css';
import { useNotify } from '../../components/notifications';
import { useConfirm } from '../../components/confirm';
import { FormTitle } from '../../components/FormTitle';
import { AppToolbar } from '../../components/AppToolbar';
import { useNcStore, summaryOf } from './store';
import { PURPOSE_LABEL, type NcCategory, type NcRule, type Severity, type DeliveryStatus } from './data';
import { NewRuleWizard, CategoryForm, RecipientForm, TemplateForm } from './dialogs';
import RuleDetailPage from './RuleDetail';

/* ── small shared bits ── */
function SevPill({ s }: { s: Severity }) {
  const label = s === 'critical' ? 'Critical' : s === 'warning' ? 'Warning' : 'Info';
  return <span className={`nc-sev ${s}`}><span className="dot" />{label}</span>;
}
function DeliveryPill({ s }: { s: DeliveryStatus }) {
  return <span className={`nc-status ${s === 'Failed' || s === 'Bounced' ? 'fail' : 'ok'}`}>{s}</span>;
}
function NcTopbar({ icon, title, sub, children }: { icon: string; title: string; sub: string; children: ReactNode }) {
  return (
    <div className="nc-topbar">
      <div className="nc-title-wrap">
        {/* Shared <FormTitle> (icon + title from the same glyph as the sidebar) —
            single source of truth; replaces the bespoke nc-title + fake load chip. */}
        <FormTitle icon={icon} title={title} />
        <div className="nc-sub">{sub}</div>
      </div>
      {/* Shared action bar — the sub-view's form-specific buttons in the left slot. */}
      <div className="nc-actions"><AppToolbar left={children} /></div>
    </div>
  );
}
function ExportButton({ onPick }: { onPick: (fmt: string) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="nc-export">
      <button className="ca-btn" onClick={() => setOpen((o) => !o)} onBlur={() => setTimeout(() => setOpen(false), 160)} title="Export to">
        <span className="tb-i tb-i-teal">📤</span> Export to ▾
      </button>
      {open && (
        <div className="nc-menu">
          {['CSV', 'Excel', 'PDF'].map((f) => (
            <div key={f} className="nc-menu-item" onMouseDown={() => { onPick(f); setOpen(false); }}><i className="ti ti-download" /> {f}</div>
          ))}
        </div>
      )}
    </div>
  );
}
function SearchBox({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) {
  return (
    <div className="list-search"><span className="tb-i">🔍</span>
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
      {value && <span className="list-search-clear" onClick={() => onChange('')}>×</span>}
    </div>
  );
}

/* ════════════════════ OVERVIEW ════════════════════ */
function OverviewPage({ onOpen }: { onOpen?: (id: string) => void }) {
  const categories = useNcStore((s) => s.categories);
  const recipients = useNcStore((s) => s.recipients);
  const templates = useNcStore((s) => s.templates);
  const deliveries = useNcStore((s) => s.deliveries);
  const addRule = useNcStore((s) => s.addRule);
  const notify = useNotify();
  const [wizard, setWizard] = useState(false);
  const sum = summaryOf(categories, recipients, templates);
  const maxRules = Math.max(1, ...categories.map((c) => c.rules.length));
  const go = (id: string) => onOpen?.(id);

  const cards = [
    { id: 'rules', icon: 'ti-adjustments-horizontal', count: sum.totalRules, name: 'Notification Rules', desc: 'Standard rules across categories — set recipients & enable the ones this lab needs.' },
    { id: 'categories', icon: 'ti-category-2', count: sum.categories, name: 'Rule Categories', desc: 'Category groupings that organise the rules list, sidebar & templates.' },
    { id: 'recipients', icon: 'ti-users-group', count: sum.recipientGroups, name: 'Recipient Groups', desc: 'Reusable recipient sets — define once, attach to many rules.' },
    { id: 'templates', icon: 'ti-mail', count: sum.templates, name: 'Email Templates', desc: 'Subject & body templates per category, with token placeholders.' },
    { id: 'delivery', icon: 'ti-mail-fast', count: sum.deliveries24h, name: 'Delivery Log', desc: 'Read-only per-recipient delivery & failures for troubleshooting.' },
  ];

  return (
    <div className="nc-root">
      <NcTopbar icon="ti-layout-dashboard" title="Notification Center" sub="Overview of every notification module — rules, categories, recipients, templates & delivery health.">
        <button className="ca-btn" onClick={() => setWizard(true)}><span className="tb-i">＋</span> New Rule</button>
        <button className="ca-btn" onClick={() => notify.success('Overview refreshed.')} title="Refresh"><span className="tb-i tb-i-green">🔄</span> Refresh</button>
      </NcTopbar>
      <div className="nc-scroll">
        <div className="nc-kpi-row">
          <div className="nc-kpi green"><div className="nc-kpi-lbl">Active Rules</div><div className="nc-kpi-val green">{sum.activeRules}</div><div className="nc-kpi-meta">of {sum.totalRules} · {sum.disabledRules} disabled</div></div>
          <div className="nc-kpi crit"><div className="nc-kpi-lbl">Require Acknowledgment</div><div className="nc-kpi-val crit">{sum.requireAck}</div><div className="nc-kpi-meta">Regulatory exceedance + hold-time</div></div>
          <div className="nc-kpi info"><div className="nc-kpi-lbl">Rule Categories</div><div className="nc-kpi-val">{sum.categories}</div><div className="nc-kpi-meta">{sum.activeCategories} active · {sum.categories - sum.activeCategories} disabled</div></div>
          <div className="nc-kpi warn"><div className="nc-kpi-lbl">Delivery Failures (24h)</div><div className="nc-kpi-val warn">{sum.deliveryFailures}</div><div className="nc-kpi-meta">of {sum.deliveries24h} sent · 98.9% delivered</div></div>
        </div>

        <div className="nc-section"><i className="ti ti-apps" /> Configuration modules <span className="nc-section-sub">Click a module to open it</span></div>
        <div className="nc-cards">
          {cards.map((c) => (
            <div key={c.id} className="nc-card" onClick={() => go(c.id)}>
              <div className="nc-card-top"><div className="nc-card-ic"><i className={`ti ${c.icon}`} /></div><div className="nc-card-count">{c.count}</div></div>
              <div className="nc-card-name">{c.name}</div>
              <div className="nc-card-desc">{c.desc}</div>
              <div className="nc-card-cta">Open <i className="ti ti-arrow-right" /></div>
            </div>
          ))}
        </div>

        <div className="nc-grid2">
          <div className="nc-panel">
            <div className="nc-panel-hd"><div><div className="nc-panel-title"><i className="ti ti-chart-bar" /> Rules by Category</div><div className="nc-panel-sub">{sum.totalRules} rules across {sum.categories} categories</div></div></div>
            <div className="nc-panel-bd">
              {categories.map((c) => (
                <div key={c.code} className={`nc-cat-row${c.active ? '' : ' off'}`} onClick={() => go(`cat-${c.code}`)}>
                  <span className="nc-cat-ic"><i className={`ti ${c.icon}`} /></span>
                  <span className="nc-cat-name">{c.name}</span>
                  <span className="nc-cat-bar"><span className="nc-cat-fill" style={{ width: `${Math.round((c.rules.length / maxRules) * 100)}%` }} /></span>
                  <span className="nc-cat-num">{c.rules.length} rule{c.rules.length === 1 ? '' : 's'}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="nc-panel">
            <div className="nc-panel-hd"><div><div className="nc-panel-title"><i className="ti ti-mail-fast" /> Recent Delivery Activity</div><div className="nc-panel-sub">Last deliveries recorded by the runtime — read-only</div></div>
              <button className="ca-btn" onClick={() => go('delivery')}>View log <i className="ti ti-arrow-right" /></button></div>
            <table className="nc-tbl">
              <thead><tr><th>Time</th><th>Rule</th><th>Channel</th><th>Status</th></tr></thead>
              <tbody>
                {deliveries.slice(0, 5).map((d, i) => (
                  <tr key={i}><td className="nc-mono">{d.time}</td><td>{d.rule}</td><td><span className="nc-chan on">{d.channel}</span></td><td><DeliveryPill s={d.status} /></td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {wizard && <NewRuleWizard categories={categories} onClose={() => setWizard(false)}
        onCreate={(cn, r) => { addRule(cn, r); setWizard(false); notify.success(`Rule "${r.name}" created.`); onOpen?.('rules'); }} />}
    </div>
  );
}

/* ════════════════════ NOTIFICATION RULES (+ category-filtered) ════════════════════ */
function RulesPage({ onOpen, filterCode }: { onOpen?: (id: string) => void; filterCode?: number }) {
  const categories = useNcStore((s) => s.categories);
  const recipients = useNcStore((s) => s.recipients);
  const templates = useNcStore((s) => s.templates);
  const addRule = useNcStore((s) => s.addRule);
  const deleteRuleCodes = useNcStore((s) => s.deleteRuleCodes);
  const toggleRule = useNcStore((s) => s.toggleRule);
  const notify = useNotify();
  const { confirm } = useConfirm();

  const [sel, setSel] = useState<Set<string>>(new Set());
  const [wizard, setWizard] = useState(false);
  const [detail, setDetail] = useState<NcRule | null>(null);
  const [search, setSearch] = useState('');
  const [chip, setChip] = useState<'all' | 'internal' | 'external' | 'disabled'>('all');

  const sum = summaryOf(categories, recipients, templates);
  const cat = filterCode ? categories.find((c) => c.code === filterCode) : undefined;
  const baseCats = filterCode ? categories.filter((c) => c.code === filterCode) : categories;
  const term = search.trim().toLowerCase();
  const matchTerm = (r: NcRule) => !term || r.name.toLowerCase().includes(term) || r.code.toLowerCase().includes(term) || r.recipients.toLowerCase().includes(term);
  const matchChip = (r: NcRule) => chip === 'all' ? true : chip === 'internal' ? !r.outbound : chip === 'external' ? !!r.outbound : !r.enabled;
  const viewCats = baseCats
    .map((c) => ({ ...c, rules: c.rules.filter((r) => matchTerm(r) && matchChip(r)) }))
    .filter((c) => c.rules.length > 0);
  const shownRules = viewCats.flatMap((c) => c.rules);
  const allCodes = shownRules.map((r) => r.code);

  const toggleSel = (code: string) => setSel((cur) => { const n = new Set(cur); if (n.has(code)) n.delete(code); else n.add(code); return n; });
  const allChecked = allCodes.length > 0 && allCodes.every((c) => sel.has(c));

  function doEdit() {
    if (sel.size !== 1) { notify.warning(sel.size ? 'Select just one rule to edit.' : 'Select a rule to edit (tick its checkbox), or click the rule name.'); return; }
    const r = shownRules.find((x) => x.code === [...sel][0]);
    if (r) setDetail(r);
  }
  function doDelete() {
    const codes = [...sel];
    if (!codes.length) { notify.warning('Select one or more rules to delete.'); return; }
    confirm({ title: codes.length === 1 ? 'Delete rule?' : 'Delete rules?', message: `Delete ${codes.length} rule${codes.length === 1 ? '' : 's'}? This cannot be undone.`, tone: 'danger', confirmLabel: 'Delete' })
      .then((ok) => { if (ok) { deleteRuleCodes(codes); setSel(new Set()); notify.success(codes.length === 1 ? 'Rule deleted.' : `${codes.length} rules deleted.`); } });
  }

  const title = cat ? cat.name : 'Notification Rules';
  const sub = cat ? `Notification Rules filtered to ${cat.name} · ${cat.rules.length} rule${cat.rules.length === 1 ? '' : 's'}` : 'Standard notification rules (MDI defaults) — fully editable. Set recipients and enable the ones this lab needs.';

  // Edit / clicking a rule name opens the full Rule Detail page (HTML parity).
  if (detail) {
    const dCat = categories.find((c) => c.rules.some((r) => r.code === detail.code))?.name ?? cat?.name ?? '';
    return <RuleDetailPage rule={detail} categoryName={dCat} onBack={() => setDetail(null)}
      onDeleted={() => { deleteRuleCodes([detail.code]); setSel(new Set()); setDetail(null); }} />;
  }

  return (
    <div className="nc-root">
      <NcTopbar icon={cat ? cat.icon : 'ti-adjustments-horizontal'} title={title} sub={sub}>
        {cat
          ? <button className="ca-btn" onClick={() => onOpen?.('rules')}><i className="ti ti-x" /> Clear filter</button>
          : <button className="ca-btn" onClick={() => notify.success('Standard default rules imported from the MDI library.')}><span className="tb-i tb-i-green">📥</span> Import from library</button>}
        <button className="ca-btn" onClick={() => setWizard(true)}><span className="tb-i">＋</span> New</button>
        <button className="ca-btn" onClick={doEdit}><span className="tb-i tb-i-blue">✎</span> Edit</button>
        <button className="ca-btn" onClick={doDelete}><span className="tb-i tb-i-red">🗑</span> Delete</button>
        <button className="ca-btn" onClick={() => notify.success('Rules refreshed.')} title="Refresh"><span className="tb-i tb-i-green">🔄</span> Refresh</button>
        <ExportButton onPick={(f) => notify.info(`Exporting rules to ${f}…`)} />
      </NcTopbar>
      <div className="nc-scroll">
        {filterCode
          ? <div className="nc-banner"><i className="ti ti-filter" /><div>Filtered to <strong>{cat?.name}</strong> — {cat?.rules.length} rule{cat?.rules.length === 1 ? '' : 's'}. Open <strong>Notification Rules</strong> (or Clear filter) to see all {sum.totalRules}.</div></div>
          : (
            <div className="nc-kpi-row">
              <div className="nc-kpi"><div className="nc-kpi-lbl">Total Rules</div><div className="nc-kpi-val">{sum.totalRules}</div><div className="nc-kpi-meta">Across {sum.categories} categories · {sum.outbound} outbound</div></div>
              <div className="nc-kpi green"><div className="nc-kpi-lbl">Active</div><div className="nc-kpi-val green">{sum.activeRules}</div><div className="nc-kpi-meta">{sum.disabledRules} disabled — enable as needed</div></div>
              <div className="nc-kpi crit"><div className="nc-kpi-lbl">Require Acknowledgment</div><div className="nc-kpi-val crit">{sum.requireAck}</div><div className="nc-kpi-meta">Regulatory exceedance + hold-time</div></div>
              <div className="nc-kpi info"><div className="nc-kpi-lbl">Need Recipients</div><div className="nc-kpi-val">{sum.needRecipients}</div><div className="nc-kpi-meta">Set before they can send</div></div>
            </div>
          )}
        <div className="nc-toolrow">
          <SearchBox value={search} onChange={setSearch} placeholder="Search rules…" />
          <div className={`nc-chip${chip === 'all' ? ' active' : ''}`} onClick={() => setChip('all')}><i className="ti ti-list" /> All</div>
          <div className={`nc-chip${chip === 'internal' ? ' active' : ''}`} onClick={() => setChip('internal')}><span className="nc-chip-dot" style={{ background: 'var(--nc-text-light)' }} /> Internal</div>
          <div className={`nc-chip${chip === 'external' ? ' active' : ''}`} onClick={() => setChip('external')}><span className="nc-chip-dot" style={{ background: 'var(--nc-blue)' }} /> External</div>
          <div className={`nc-chip${chip === 'disabled' ? ' active' : ''}`} onClick={() => setChip('disabled')}><i className="ti ti-toggle-left" /> Disabled</div>
        </div>
        <div className="nc-table-wrap">
          <table className="nc-tbl">
            <thead><tr>
              <th className="nc-cb"><input type="checkbox" checked={allChecked} onChange={(e) => setSel(e.target.checked ? new Set(allCodes) : new Set())} /></th>
              <th>Rule Name</th><th>Rule Code</th><th>Purpose</th><th>Severity</th><th>Targets</th><th>Recipients</th><th>Ack</th><th>Enabled</th>
            </tr></thead>
            <tbody>
              {viewCats.map((c) => (
                <RuleGroupRows key={c.code} cat={c} sel={sel} onToggleSel={toggleSel} onOpenDetail={setDetail} onToggleRule={(code) => { toggleRule(code); }} />
              ))}
              {shownRules.length === 0 && <tr><td colSpan={9} style={{ textAlign: 'center', color: 'var(--nc-text-light)', padding: 24 }}>No rules match “{search}”.</td></tr>}
            </tbody>
          </table>
          <div className="nc-footer">
            <div className="nc-foot-left">{filterCode
              ? `Showing ${shownRules.length} rule${shownRules.length === 1 ? '' : 's'} in ${cat?.name}`
              : `Showing ${shownRules.length} of ${sum.totalRules} rules · ${sum.activeRules} enabled · ${sum.disabledRules} disabled`}</div>
            <div className="nc-pg"><button className="nc-pg-btn active">1</button></div>
          </div>
        </div>
      </div>
      {wizard && <NewRuleWizard categories={categories} defaultCategory={cat?.name} onClose={() => setWizard(false)}
        onCreate={(cn, r) => { addRule(cn, r); setWizard(false); notify.success(`Rule "${r.name}" created.`); }} />}
    </div>
  );
}

function RuleGroupRows({ cat, sel, onToggleSel, onOpenDetail, onToggleRule }: {
  cat: NcCategory; sel: Set<string>; onToggleSel: (code: string) => void; onOpenDetail: (r: NcRule) => void; onToggleRule: (code: string) => void;
}) {
  return (
    <>
      <tr className="nc-catgroup"><td colSpan={9}><i className={`ti ${cat.icon}`} />{cat.name}<span className="nc-catgroup-count">{cat.rules.length} rules</span></td></tr>
      {cat.rules.map((r) => (
        <tr key={r.code}>
          <td className="nc-cb"><input type="checkbox" checked={sel.has(r.code)} onChange={() => onToggleSel(r.code)} /></td>
          <td><span className="nc-rowlink" onClick={() => onOpenDetail(r)}>{r.name}</span></td>
          <td><span className="nc-mono">{r.code}</span></td>
          <td><span className={`nc-pp ${r.purpose}`}>{PURPOSE_LABEL[r.purpose]}</span></td>
          <td>{r.outbound ? <span className="nc-comm"><span className="dot" />Outbound</span> : r.severity ? <SevPill s={r.severity} /> : <span className="nc-dash">—</span>}</td>
          <td>{r.targets ? <span className="nc-tag">{r.targets}</span> : <span className="nc-dash">—</span>}</td>
          <td>{r.externalRecipient ? <span className="nc-ext">{r.recipients}</span> : r.recipients}</td>
          <td>{r.ack ? <i className="ti ti-check" style={{ color: 'var(--nc-crit)' }} /> : <span className="nc-dash">—</span>}</td>
          <td><span className={`nc-toggle${r.enabled ? ' on' : ''}`} role="button" title="Toggle enabled" onClick={() => onToggleRule(r.code)} /></td>
        </tr>
      ))}
    </>
  );
}

/* ════════════════════ RULE CATEGORIES ════════════════════ */
function CategoriesPage() {
  const categories = useNcStore((s) => s.categories);
  const addCategory = useNcStore((s) => s.addCategory);
  const updateCategory = useNcStore((s) => s.updateCategory);
  const deleteCategoryCodes = useNcStore((s) => s.deleteCategoryCodes);
  const notify = useNotify();
  const { confirm } = useConfirm();
  const [sel, setSel] = useState<Set<number>>(new Set());
  const [form, setForm] = useState<{ open: boolean; edit?: NcCategory }>({ open: false });

  const nextCode = Math.max(0, ...categories.map((c) => c.code)) + 1;
  const nextOrder = Math.max(0, ...categories.map((c) => c.order)) + 10;
  const activeCount = categories.filter((c) => c.active).length;
  const toggleSel = (code: number) => setSel((cur) => { const n = new Set(cur); if (n.has(code)) n.delete(code); else n.add(code); return n; });

  function del(codes: number[]) {
    if (!codes.length) { notify.warning('Select one or more categories to delete.'); return; }
    const blocked = categories.find((c) => codes.includes(c.code) && c.rules.length > 0);
    if (blocked) { notify.error(`“${blocked.name}” still has ${blocked.rules.length} rule(s) — a category can only be deleted once no rule references it.`); return; }
    confirm({ title: 'Delete category?', message: `Delete ${codes.length} categor${codes.length === 1 ? 'y' : 'ies'}?`, tone: 'danger', confirmLabel: 'Delete' })
      .then((ok) => { if (ok) { deleteCategoryCodes(codes); setSel(new Set()); notify.success(codes.length === 1 ? 'Category deleted.' : `${codes.length} categories deleted.`); } });
  }
  function doEdit() {
    if (sel.size !== 1) { notify.warning(sel.size ? 'Select just one category to edit.' : 'Select a category to edit.'); return; }
    setForm({ open: true, edit: categories.find((c) => c.code === [...sel][0]) });
  }

  return (
    <div className="nc-root">
      <NcTopbar icon="ti-category-2" title="Rule Categories" sub="Reference data — the categories rules are grouped under, and the Category data source for rule configuration.">
        <button className="ca-btn" onClick={() => setForm({ open: true })}><span className="tb-i">＋</span> New</button>
        <button className="ca-btn" onClick={doEdit}><span className="tb-i tb-i-blue">✎</span> Edit</button>
        <button className="ca-btn" onClick={() => del([...sel])}><span className="tb-i tb-i-red">🗑</span> Delete</button>
        <button className="ca-btn" onClick={() => notify.success('Categories refreshed.')} title="Refresh"><span className="tb-i tb-i-green">🔄</span> Refresh</button>
        <ExportButton onPick={(f) => notify.info(`Exporting categories to ${f}…`)} />
      </NcTopbar>
      <div className="nc-scroll">
        <div className="nc-banner"><i className="ti ti-info-circle" /><div>These categories populate the <strong>Category</strong> dropdown in rule configuration and group the rules list &amp; sidebar. A category can only be deleted once no rule references it.</div></div>
        <div className="nc-table-wrap">
          <table className="nc-tbl">
            <thead><tr><th className="nc-cb" /><th>Order</th><th>Icon</th><th>Code</th><th>Name</th><th>Rules</th><th>Active</th><th style={{ textAlign: 'right' }}>Actions</th></tr></thead>
            <tbody>
              {categories.map((c) => (
                <tr key={c.code}>
                  <td className="nc-cb"><input type="checkbox" checked={sel.has(c.code)} onChange={() => toggleSel(c.code)} /></td>
                  <td>{c.order}</td>
                  <td><span className="nc-iccell"><i className={`ti ${c.icon}`} /></span></td>
                  <td><span className="nc-mono">{c.code}</span></td>
                  <td>{c.name}</td>
                  <td><span className="nc-tag">{c.rules.length}</span></td>
                  <td><span className={`nc-toggle${c.active ? ' on' : ''}`} role="button" title="Toggle active" onClick={() => updateCategory(c.code, { active: !c.active })} /></td>
                  <td><div className="nc-rowacts">
                    <button className="nc-iconbtn" title="Edit" onClick={() => setForm({ open: true, edit: c })}><i className="ti ti-edit" /></button>
                    <button className="nc-iconbtn del" title="Delete" onClick={() => del([c.code])}><i className="ti ti-trash" /></button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="nc-footer"><div className="nc-foot-left">{categories.length} categories · {activeCount} active</div><div className="nc-pg"><button className="nc-pg-btn active">1</button></div></div>
        </div>
      </div>
      {form.open && <CategoryForm initial={form.edit} nextCode={nextCode} nextOrder={nextOrder} onClose={() => setForm({ open: false })}
        onSave={(c) => { if (form.edit) { updateCategory(form.edit.code, c); notify.success('Category updated.'); } else { addCategory(c); notify.success('Category created.'); } setForm({ open: false }); }} />}
    </div>
  );
}

/* ════════════════════ RECIPIENT GROUPS ════════════════════ */
function RecipientsPage() {
  const recipients = useNcStore((s) => s.recipients);
  const addRecipient = useNcStore((s) => s.addRecipient);
  const updateRecipient = useNcStore((s) => s.updateRecipient);
  const deleteRecipientNames = useNcStore((s) => s.deleteRecipientNames);
  const notify = useNotify();
  const { confirm } = useConfirm();
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [form, setForm] = useState<{ open: boolean; edit?: typeof recipients[number] }>({ open: false });
  const toggleSel = (name: string) => setSel((cur) => { const n = new Set(cur); if (n.has(name)) n.delete(name); else n.add(name); return n; });

  function del(names: string[]) {
    if (!names.length) { notify.warning('Select one or more groups to delete.'); return; }
    confirm({ title: 'Delete recipient group?', message: `Delete ${names.length} group${names.length === 1 ? '' : 's'}?`, tone: 'danger', confirmLabel: 'Delete' })
      .then((ok) => { if (ok) { deleteRecipientNames(names); setSel(new Set()); notify.success(names.length === 1 ? 'Group deleted.' : `${names.length} groups deleted.`); } });
  }
  function doEdit() {
    if (sel.size !== 1) { notify.warning(sel.size ? 'Select just one group to edit.' : 'Select a group to edit.'); return; }
    setForm({ open: true, edit: recipients.find((r) => r.name === [...sel][0]) });
  }

  return (
    <div className="nc-root">
      <NcTopbar icon="ti-users-group" title="Recipient Groups" sub="Reusable recipient sets that rules reference — define once, attach to many rules.">
        <button className="ca-btn" onClick={() => setForm({ open: true })}><span className="tb-i">＋</span> New</button>
        <button className="ca-btn" onClick={doEdit}><span className="tb-i tb-i-blue">✎</span> Edit</button>
        <button className="ca-btn" onClick={() => del([...sel])}><span className="tb-i tb-i-red">🗑</span> Delete</button>
        <button className="ca-btn" onClick={() => notify.success('Recipient groups refreshed.')} title="Refresh"><span className="tb-i tb-i-green">🔄</span> Refresh</button>
        <ExportButton onPick={(f) => notify.info(`Exporting recipient groups to ${f}…`)} />
      </NcTopbar>
      <div className="nc-scroll">
        <div className="nc-banner"><i className="ti ti-info-circle" /><div>Attach a whole set of recipients to a rule in one pick. Edit a group here and every rule using it updates automatically.</div></div>
        <div className="nc-table-wrap">
          <table className="nc-tbl">
            <thead><tr><th className="nc-cb" /><th>Group Name</th><th>Members</th><th>Channels</th><th>Used by</th><th style={{ textAlign: 'right' }}>Actions</th></tr></thead>
            <tbody>
              {recipients.map((g) => (
                <tr key={g.name}>
                  <td className="nc-cb"><input type="checkbox" checked={sel.has(g.name)} onChange={() => toggleSel(g.name)} /></td>
                  <td><span className="nc-rowlink" onClick={() => setForm({ open: true, edit: g })}>{g.name}</span></td>
                  <td style={{ color: 'var(--nc-text-mid)' }}>{g.members}</td>
                  <td>{g.channels.map((ch) => <span key={ch} className="nc-chan on" style={{ marginRight: 4 }}>{ch}</span>)}</td>
                  <td><span className="nc-tag">{g.usedBy} rules</span></td>
                  <td><div className="nc-rowacts">
                    <button className="nc-iconbtn" title="Edit" onClick={() => setForm({ open: true, edit: g })}><i className="ti ti-edit" /></button>
                    <button className="nc-iconbtn del" title="Delete" onClick={() => del([g.name])}><i className="ti ti-trash" /></button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="nc-footer"><div className="nc-foot-left">{recipients.length} groups</div><div className="nc-pg"><button className="nc-pg-btn active">1</button></div></div>
        </div>
      </div>
      {form.open && <RecipientForm initial={form.edit} onClose={() => setForm({ open: false })}
        onSave={(g) => { if (form.edit) { updateRecipient(form.edit.name, g); notify.success('Group updated.'); } else { addRecipient(g); notify.success('Group created.'); } setForm({ open: false }); }} />}
    </div>
  );
}

/* ════════════════════ EMAIL TEMPLATES ════════════════════ */
function TemplatesPage() {
  const categories = useNcStore((s) => s.categories);
  const templates = useNcStore((s) => s.templates);
  const addTemplate = useNcStore((s) => s.addTemplate);
  const updateTemplate = useNcStore((s) => s.updateTemplate);
  const deleteTemplateNames = useNcStore((s) => s.deleteTemplateNames);
  const notify = useNotify();
  const { confirm } = useConfirm();
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [form, setForm] = useState<{ open: boolean; edit?: typeof templates[number] }>({ open: false });
  const toggleSel = (name: string) => setSel((cur) => { const n = new Set(cur); if (n.has(name)) n.delete(name); else n.add(name); return n; });
  const activeCount = templates.filter((t) => t.active).length;

  function del(names: string[]) {
    if (!names.length) { notify.warning('Select one or more templates to delete.'); return; }
    confirm({ title: 'Delete template?', message: `Delete ${names.length} template${names.length === 1 ? '' : 's'}?`, tone: 'danger', confirmLabel: 'Delete' })
      .then((ok) => { if (ok) { deleteTemplateNames(names); setSel(new Set()); notify.success(names.length === 1 ? 'Template deleted.' : `${names.length} templates deleted.`); } });
  }
  function doEdit() {
    if (sel.size !== 1) { notify.warning(sel.size ? 'Select just one template to edit.' : 'Select a template to edit.'); return; }
    setForm({ open: true, edit: templates.find((t) => t.name === [...sel][0]) });
  }

  return (
    <div className="nc-root">
      <NcTopbar icon="ti-mail" title="Email Templates" sub="Subject & body templates per category, with token placeholders.">
        <button className="ca-btn" onClick={() => setForm({ open: true })}><span className="tb-i">＋</span> New</button>
        <button className="ca-btn" onClick={doEdit}><span className="tb-i tb-i-blue">✎</span> Edit</button>
        <button className="ca-btn" onClick={() => del([...sel])}><span className="tb-i tb-i-red">🗑</span> Delete</button>
        <button className="ca-btn" onClick={() => notify.success('Templates refreshed.')} title="Refresh"><span className="tb-i tb-i-green">🔄</span> Refresh</button>
        <ExportButton onPick={(f) => notify.info(`Exporting templates to ${f}…`)} />
      </NcTopbar>
      <div className="nc-scroll">
        <div className="nc-banner"><i className="ti ti-info-circle" /><div>Templates render the emails sent when a rule fires. Use tokens like <span className="nc-tok">{'{{SampleId}}'}</span> <span className="nc-tok">{'{{Client}}'}</span> <span className="nc-tok">{'{{Result}}'}</span> — filled in at send time.</div></div>
        <div className="nc-table-wrap">
          <table className="nc-tbl">
            <thead><tr><th className="nc-cb" /><th>Template Name</th><th>Category</th><th>Subject</th><th>Active</th><th style={{ textAlign: 'right' }}>Actions</th></tr></thead>
            <tbody>
              {templates.map((t) => (
                <tr key={t.name}>
                  <td className="nc-cb"><input type="checkbox" checked={sel.has(t.name)} onChange={() => toggleSel(t.name)} /></td>
                  <td><span className="nc-rowlink" onClick={() => setForm({ open: true, edit: t })}>{t.name}</span></td>
                  <td>{t.category}</td>
                  <td style={{ color: 'var(--nc-text-mid)' }}>{t.subject}</td>
                  <td><span className={`nc-toggle${t.active ? ' on' : ''}`} role="button" title="Toggle active" onClick={() => updateTemplate(t.name, { active: !t.active })} /></td>
                  <td><div className="nc-rowacts">
                    <button className="nc-iconbtn" title="Edit" onClick={() => setForm({ open: true, edit: t })}><i className="ti ti-edit" /></button>
                    <button className="nc-iconbtn del" title="Delete" onClick={() => del([t.name])}><i className="ti ti-trash" /></button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="nc-footer"><div className="nc-foot-left">{templates.length} templates · {activeCount} active</div><div className="nc-pg"><button className="nc-pg-btn active">1</button></div></div>
        </div>
      </div>
      {form.open && <TemplateForm initial={form.edit} categoryNames={categories.map((c) => c.name)} onClose={() => setForm({ open: false })}
        onSave={(t) => { if (form.edit) { updateTemplate(form.edit.name, t); notify.success('Template updated.'); } else { addTemplate(t); notify.success('Template created.'); } setForm({ open: false }); }} />}
    </div>
  );
}

/* ════════════════════ DELIVERY LOG (read-only) ════════════════════ */
function DeliveryPage() {
  const deliveries = useNcStore((s) => s.deliveries);
  const notify = useNotify();
  return (
    <div className="nc-root">
      <NcTopbar icon="ti-mail-fast" title="Delivery Log" sub="Read-only · per-recipient delivery & failures, for troubleshooting.">
        <button className="ca-btn" onClick={() => notify.success('Delivery log refreshed.')} title="Refresh"><span className="tb-i tb-i-green">🔄</span> Refresh</button>
        <ExportButton onPick={(f) => notify.info(`Exporting delivery log to ${f}…`)} />
      </NcTopbar>
      <div className="nc-scroll">
        <div className="nc-banner"><i className="ti ti-info-circle" /><div>Troubleshoot email / SMS / Teams delivery failures and bounces here. This screen is <strong>read-only</strong>.</div></div>
        <div className="nc-table-wrap">
          <table className="nc-tbl">
            <thead><tr><th>Time</th><th>Rule</th><th>Channel</th><th>Recipient</th><th>Status</th><th>Detail</th></tr></thead>
            <tbody>
              {deliveries.map((d, i) => (
                <tr key={i}><td className="nc-mono">{d.time}</td><td>{d.rule}</td><td><span className="nc-chan on">{d.channel}</span></td><td>{d.recipient}</td><td><DeliveryPill s={d.status} /></td><td style={{ color: 'var(--nc-text-mid)' }}>{d.detail}</td></tr>
              ))}
            </tbody>
          </table>
          <div className="nc-footer"><div className="nc-foot-left">Page 1 of 18 (524 deliveries · 6 failed)</div><div className="nc-pg"><button className="nc-pg-btn active">1</button><button className="nc-pg-btn">2</button><button className="nc-pg-btn">3</button></div></div>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════ host ════════════════════ */
export default function NotificationCenterPage({ subId, onOpen }: { subId: string; onOpen?: (id: string) => void }) {
  const catMatch = /^cat-(\d+)$/.exec(subId);
  if (catMatch) return <RulesPage onOpen={onOpen} filterCode={Number(catMatch[1])} />;
  switch (subId) {
    case 'rules': return <RulesPage onOpen={onOpen} />;
    case 'categories': return <CategoriesPage />;
    case 'recipients': return <RecipientsPage />;
    case 'templates': return <TemplatesPage />;
    case 'delivery': return <DeliveryPage />;
    default: return <OverviewPage onOpen={onOpen} />;
  }
}
